import React, { useEffect, useId, useRef, useState } from "react";
import { Search, X, LoaderCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { queryClient } from "@/hooks/use-query-client";
import type { QueryKey } from "@tanstack/react-query";

export type GroupedSection<T> = {
  data: T[];
  hasMore: boolean;
  title: string;
};

export type GroupedResponse<T> = Record<string, GroupedSection<T> | null>;

type GroupedSearchAutocompleteProps<T> = {
  label?: string;
  placeholder?: string;
  placeholderIcon?: React.ComponentType;
  value?: string;
  onSearch?: (keyword: string) => void;
  onSelect?: (result: T) => void;
  onClear?: () => void;
  queryFunction: (query: string) => {
    data?: GroupedResponse<T>;
    isPending: boolean;
    refetch: () => void;
  };
  formatResult: (
    sectionKey: keyof GroupedResponse<T>,
    item: T,
    index: number,
  ) => React.ReactNode;
  className?: string;
  autoFocus?: boolean;
  minSearchLength?: number;
  debounceTime?: number;
  onLoadMore?: (section: GroupedSection<T>, searchQuery: string) => void;
  cacheQueryKey: QueryKey;
  disabled?: boolean;
};

export default function GroupedSearchAutocomplete<T>({
  label,
  placeholder = "Search",
  placeholderIcon: PlaceholderIcon = Search,
  value,
  onSearch,
  onSelect,
  onClear,
  queryFunction,
  formatResult,
  className,
  autoFocus = false,
  minSearchLength = 3,
  debounceTime = 500,
  onLoadMore,
  cacheQueryKey,
  disabled = false,
}: GroupedSearchAutocompleteProps<T>) {
  // Stable, collision-free id so the optional <Label> can be associated with the field
  // (WCAG 3.3.2 Labels or Instructions — A). This component is rendered many times per
  // page, so a hard-coded id would produce duplicates (4.1.1 Parsing — A).
  const inputId = useId();
  const [searchString, setSearchString] = useState<string>(value ?? "");
  const [showResults, setShowResults] = useState<boolean>(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const componentRef = useRef<HTMLDivElement>(null);
  const queryResult = queryFunction(searchString);
  const { data: queryData, isPending } = queryResult;

  const isSearchValid = searchString?.length >= minSearchLength;
  const groupData = isSearchValid ? queryData : undefined;
  const shouldShowDropdown = isSearchValid && showResults && groupData;

  const isEmptyResults =
    groupData &&
    Object.values(groupData)?.every(
      (section) => !section || section?.data?.length === 0,
    );

  useEffect(() => {
    setSearchString(value ?? "");
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchString(value);
    onSearch?.(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value?.length >= minSearchLength) {
      searchTimeoutRef.current = setTimeout(() => {
        queryResult?.refetch();
        setShowResults(true);
      }, debounceTime);
    } else {
      if (value?.length === 0) {
        queryClient.removeQueries({ queryKey: cacheQueryKey });
      }
      setShowResults(false);
    }
  };

  const handleClear = () => {
    queryClient.removeQueries({ queryKey: cacheQueryKey });
    setSearchString("");
    setShowResults(false);
    onClear?.();

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  const handleSelectItem = (result: T) => {
    setShowResults(false);
    onSelect?.(result);
  };

  return (
    <div className={`space-y-2 ${className ?? ""}`} ref={componentRef}>
      {/* 3.3.2 Labels or Instructions — A: the <Label> had no htmlFor and the field no
          id, so the label was orphaned and the search box unnamed. When no visible label
          is supplied, the placeholder text is promoted to the accessible name so the field
          is never anonymous. */}
      {label && (
        <Label htmlFor={inputId} className="text-smtext-md font-semibold">
          {label}
        </Label>
      )}
      <div className="relative">
        <Input
          id={inputId}
          aria-label={label ? undefined : (placeholder ?? "Search")}
          type="text"
          value={searchString}
          disabled={disabled}
          onChange={handleInputChange}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onFocus={() => {
            if (searchString?.length >= minSearchLength) {
              setShowResults(true);
            }
          }}
          onBlur={() => setTimeout(() => setShowResults(false), 200)}
          className="w-full pr-10 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {isSearchValid && isPending ? (
            <LoaderCircle className="size-4 animate-spin text-muted-foreground" />
          ) : searchString ? (
            // 2.1.1 Keyboard — A / 4.1.2 — A: the clear control was a bare Lucide <svg>
            // with an onClick — not focusable, no role, no name.
            <button
              type="button"
              onClick={handleClear}
              aria-label="Clear search"
              className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            >
              <X
                aria-hidden="true"
                className="size-4 cursor-pointer text-muted-foreground hover:text-foreground"
              />
            </button>
          ) : (
            <div className="flex size-4 items-center justify-center text-muted-foreground">
              <PlaceholderIcon />
            </div>
          )}
        </div>

        {shouldShowDropdown && (
          <Card className="absolute z-20 mt-2 w-full overflow-hidden">
            <CardContent className="p-0">
              <ul className="max-h-[360px] overflow-y-auto">
                {Object.entries(groupData ?? {})?.map(
                  ([sectionKey, section]) => {
                    if (!section || section?.data?.length === 0) return null;
                    return (
                      <li key={sectionKey}>
                        <div className="bg-muted px-4 py-2 text-sm font-medium text-muted-foreground">
                          {section?.title}
                        </div>
                        {/* 2.1.1 Keyboard — A / 4.1.2 — A: search results were
                            <div onClick>, so a keyboard user could type a query but could
                            not select any of the results it returned. */}
                        {section?.data?.map((item, itemIndex) => (
                          <button
                            type="button"
                            key={itemIndex}
                            className="w-full cursor-pointer px-4 py-2 text-left hover:bg-primary-extraLight hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                            onClick={() => {
                              setSearchString("");
                              handleSelectItem(item);
                            }}
                          >
                            {formatResult(sectionKey, item, itemIndex)}
                          </button>
                        ))}
                        {section?.hasMore && onLoadMore && (
                          <div className="flex justify-center border-t p-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSearchString("");
                                onLoadMore(section, searchString);
                              }}
                              className="w-full"
                            >
                              View More
                            </Button>
                          </div>
                        )}
                      </li>
                    );
                  },
                )}

                {isEmptyResults && !isPending && (
                  <li className="p-4 text-sm text-muted-foreground">
                    No results found
                  </li>
                )}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
